import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, 
  X, 
  ChevronDown, 
  ShoppingBag, 
  Megaphone, 
  Cpu, 
  Coins, 
  Building2, 
  GraduationCap, 
  Video,
  Landmark,
  Brain,
  Eye,
  MessageSquare,
  Database,
  Cloud,
  GitMerge,
  ArrowRight,
  Sparkles,
  Leaf,
  Activity,
  TrendingUp,
  Zap,
  Lightbulb,
  ArrowUpRight,
  Shield,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";

import logo from "@/assets/logo.png";

const navLinks = [
  { name: "Home", path: "/" },
  { 
    name: "Industries", 
    isDropdown: true,
    dropdownId: "industries",
    columns: [
      {
        title: "",
        items: [
          { 
            id: "fast-moving-consumer-goods", 
            name: "FMCG", 
            path: "/industries/fast-moving-consumer-goods", 
            icon: ShoppingBag,
            color: "bg-blue-50 text-blue-600"
          },
          { 
            id: "marketing", 
            name: "Marketing", 
            path: "/industries/marketing", 
            icon: Megaphone,
            color: "bg-amber-50 text-amber-600"
          },
          { 
            id: "consumer-electronics", 
            name: "Electronics", 
            path: "/industries/consumer-electronics", 
            icon: Cpu,
            color: "bg-emerald-50 text-emerald-600"
          },
          { 
            id: "digital-media", 
            name: "Media", 
            path: "/industries/digital-media", 
            icon: Video,
            color: "bg-purple-50 text-purple-600"
          }
        ]
      },
      {
        title: "",
        items: [
          { 
            id: "fintech", 
            name: "Fintech", 
            path: "/industries/fintech", 
            icon: Coins,
            color: "bg-teal-50 text-teal-600"
          },
          { 
            id: "construction", 
            name: "Construction", 
            path: "/industries/construction", 
            icon: Building2,
            color: "bg-rose-50 text-rose-600"
          },
          { 
            id: "edtech", 
            name: "Edtech", 
            path: "/industries/edtech", 
            icon: GraduationCap,
            color: "bg-sky-50 text-sky-600"
          },
          { 
            id: "government", 
            name: "Government", 
            path: "/industries/government", 
            icon: Landmark,
            color: "bg-indigo-50 text-indigo-600"
          }
        ]
      }
    ],
    callout: {
      title: "Case Studies",
      desc: "Explore our production-ready enterprise solutions.",
      linkText: "VANCO Success Stories",
      linkPath: "/case-studies",
      gradient: "from-[#1e3a8a] via-[#1b2f68] to-indigo-950",
      lightText: true
    }
  },
  { 
    name: "Expertise", 
    isDropdown: true,
    dropdownId: "expertise",
    columns: [
      {
        title: "AI & Engineering",
        items: [
          { 
            id: "generative-ai", 
            name: "Generative AI & LLMs", 
            path: "/expertise", 
            icon: Brain,
            color: "bg-blue-50 text-blue-600"
          },
          { 
            id: "computer-vision", 
            name: "Computer Vision", 
            path: "/expertise", 
            icon: Eye,
            color: "bg-amber-50 text-amber-600"
          },
          { 
            id: "conversational-ai", 
            name: "Conversational AI", 
            path: "/expertise", 
            icon: MessageSquare,
            color: "bg-emerald-50 text-emerald-600"
          },
          { 
            id: "mlops", 
            name: "ML Operations (MLOps)", 
            path: "/expertise", 
            icon: Cpu,
            color: "bg-purple-50 text-purple-600"
          },
          { 
            id: "data-governance", 
            name: "Data Governance", 
            path: "/expertise", 
            icon: Database,
            color: "bg-rose-50 text-rose-600"
          }
        ]
      },
      {
        title: "Functions & Cloud",
        items: [
          { 
            id: "supply-chain", 
            name: "Supply Chain Analytics", 
            path: "/expertise", 
            icon: GitMerge,
            color: "bg-indigo-50 text-indigo-600"
          },
          { 
            id: "finance-analytics", 
            name: "Finance Analytics", 
            path: "/expertise", 
            icon: Coins,
            color: "bg-teal-50 text-teal-600"
          },
          { 
            id: "esg-analytics", 
            name: "ESG & Sustainability", 
            path: "/expertise", 
            icon: Leaf,
            color: "bg-green-50 text-green-600"
          },
          { 
            id: "cloud-architecture", 
            name: "Cloud Architecture", 
            path: "/expertise", 
            icon: Cloud,
            color: "bg-sky-50 text-sky-600"
          }
        ]
      },
      {
        title: "Products & Labs",
        items: [
          { 
            id: "minemitra-chatbot", 
            name: "MineMitra", 
            path: "/case-studies/minemitra-chatbot", 
            icon: MessageSquare,
            color: "bg-amber-50 text-amber-600"
          },
          { 
            id: "onprem-document-ai", 
            name: "Sovereign Document AI", 
            path: "/case-studies/onprem-document-ai", 
            icon: Shield,
            color: "bg-blue-50 text-blue-600"
          },
          { 
            id: "whatsapp-ai-pitch-bot", 
            name: "WhatsApp Pitch Bot", 
            path: "/case-studies/whatsapp-ai-pitch-bot", 
            icon: MessageSquare,
            color: "bg-emerald-50 text-emerald-600"
          },
          { 
            id: "sovereign-mining-detection", 
            name: "Sovereign ANPR & Vision", 
            path: "/case-studies/sovereign-mining-detection", 
            icon: Video,
            color: "bg-slate-50 text-[#243b7f]"
          },
          { 
            id: "ai-invoicing-engine", 
            name: "AI Invoicing Engine", 
            path: "/case-studies/ai-invoicing-engine", 
            icon: FileText,
            color: "bg-teal-50 text-teal-600"
          }
        ]
      }
    ],
    callout: {
      title: "AI Workshop",
      desc: "Collaborate with our team to build your AI roadmap.",
      linkText: "Book an AI Session",
      linkPath: "/contact",
      gradient: "from-[#f59e0b] via-[#d97706] to-[#b45309]",
      lightText: true
    }
  },
  { name: "About Us", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Case Studies", path: "/case-studies" },
];

const darkHeroIndustryPaths = [
  "/industries/fast-moving-consumer-goods",
  "/industries/marketing",
  "/industries/consumer-electronics",
  "/industries/fintech",
  "/industries/construction",
  "/industries/edtech"
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'industries', 'expertise', or null
  // Close the desktop dropdown only after a short grace period, so moving the
  // cursor from the trigger down into the panel (or briefly off the edge) doesn't dismiss it.
  const closeTimer = useRef(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openDropdown = (id) => {
    cancelClose();
    setActiveDropdown(id);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 180);
  };
  useEffect(() => cancelClose, []);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null); // 'industries', 'expertise', or null
  const location = useLocation();

  const isDarkHeroPage = darkHeroIndustryPaths.includes(location.pathname);
  const isNavDark = isDarkHeroPage && !scrolled && activeDropdown === null;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveMobileDropdown(null);
    setActiveDropdown(null);
  }, [location]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop Dropdown Backdrop Blur Overlay */}
      <AnimatePresence>
        {activeDropdown !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 top-0 bg-slate-900/5 backdrop-blur-[4px] pointer-events-none z-30"
          />
        )}
      </AnimatePresence>

      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed z-50 left-4 right-4 max-w-7xl mx-auto border font-season-mix transition-all duration-300 rounded-full ${
          activeDropdown !== null
            ? "top-2 bg-white/95 border-slate-200/60 shadow-lg text-slate-800"
            : scrolled
            ? "top-2 bg-white/85 backdrop-blur-xl shadow-lg border-slate-200/50 text-slate-800"
            : isDarkHeroPage
            ? "top-4 bg-slate-950/80 backdrop-blur-xl shadow-xl border-slate-800/80 text-white"
            : "top-4 bg-white/35 backdrop-blur-xl shadow-md border-white/30 text-slate-800"
        }`}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        data-testid="navbar"
      >
        <div className="w-full px-6 md:px-8 relative z-40">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center" 
              onMouseEnter={() => setActiveDropdown(null)}
              data-testid="navbar-logo"
            >
              <img
                src={logo}
                alt="VANCO"
                className={`h-8 md:h-9 w-auto transition-all duration-300 ${
                  isNavDark ? "brightness-0 invert" : ""
                }`}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-8">
              {navLinks.map((link) => {
                if (link.isDropdown) {
                  const isDropdownActive = link.dropdownId === "industries" 
                    ? location.pathname.startsWith("/industries")
                    : location.pathname.startsWith("/expertise");

                  return (
                    <div
                      key={link.name}
                      className="relative py-2"
                      onMouseEnter={() => openDropdown(link.dropdownId)}
                    >
                      <button
                        className={`flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wider transition-colors ${
                          isDropdownActive
                            ? isNavDark ? "text-[#f7c550]" : "text-[#243b7f]"
                            : isNavDark ? "text-white hover:text-[#f7c550]" : "text-slate-700 hover:text-[#243b7f]"
                        }`}
                        data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {link.name}
                        <ChevronDown
                          size={12}
                          className={`transition-transform duration-300 ${
                            activeDropdown === link.dropdownId ? "rotate-180" : ""
                          } ${isNavDark ? "text-white" : ""}`}
                        />
                        {/* Active indicator - yellow underline */}
                        {isDropdownActive && (
                          <motion.div
                            layoutId="navActiveIndicator"
                            className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#f7c550] rounded-full"
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        )}
                      </button>
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onMouseEnter={() => setActiveDropdown(null)}
                    className={`relative text-[13px] font-bold uppercase tracking-wider transition-colors ${
                      isActive(link.path)
                        ? isNavDark ? "text-[#f7c550]" : "text-[#243b7f]"
                        : isNavDark ? "text-white hover:text-[#f7c550]" : "text-slate-700 hover:text-[#243b7f]"
                    }`}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                    {/* Active indicator - yellow underline */}
                    {isActive(link.path) && (
                      <motion.div
                        layoutId="navActiveIndicator"
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#f7c550] rounded-full"
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div 
              className="hidden xl:flex items-center gap-3"
              onMouseEnter={() => setActiveDropdown(null)}
            >
              <Link to="/contact">
                <Button
                  className={`rounded-full px-5 py-2 text-[13px] font-bold uppercase tracking-wider transition-all duration-300 ${
                    isNavDark
                      ? "bg-[#f7c550] hover:bg-[#f5b82e] text-slate-950 shadow-md shadow-amber-500/20"
                      : "bg-[#243b7f] hover:bg-[#1e3a8a] text-white hover:shadow-lg hover:shadow-blue-900/20"
                  }`}
                  data-testid="nav-cta-get-started"
                >
                  Get Started
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className={`rounded-full px-5 py-2 text-[13px] font-bold uppercase tracking-wider transition-all duration-300 ${
                    isNavDark
                      ? "bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur"
                      : "bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 hover:shadow-sm"
                  }`}
                  data-testid="nav-cta-contact-us"
                >
                  Contact Us
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`xl:hidden p-2 transition-colors ${
                isNavDark ? "text-white hover:text-[#f7c550]" : "text-slate-700 hover:text-[#243b7f]"
              }`}
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={22} className={isNavDark ? "text-white" : ""} /> : <Menu size={22} className={isNavDark ? "text-white" : ""} />}
            </button>
          </div>
        </div>

        {/* Expanded Dropdown Content (Desktop Only) */}
        <AnimatePresence>
          {activeDropdown !== null && (
            <div
              className="hidden xl:block absolute left-0 right-0 top-full pt-2 px-4 z-50"
              onMouseEnter={cancelClose}
            >
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                transition={{ duration: 0.2 }}
                className={`bg-white border border-slate-100 rounded-[32px] shadow-2xl p-6 mx-auto ${
                  activeDropdown === "expertise" ? "max-w-[980px]" : "max-w-[880px]"
                } text-left`}
              >
                {(() => {
                  const activeLink = navLinks.find(l => l.dropdownId === activeDropdown);
                  if (!activeLink) return null;

                  return (
                    <div className="grid grid-cols-12 gap-8 items-center">
                      {/* Left Columns (Links) */}
                      <div className={`${
                        activeLink.columns.length === 3 ? "col-span-8 grid-cols-3" : "col-span-7 grid-cols-2"
                      } grid gap-6`}>
                        {activeLink.columns.map((column, colIdx) => (
                          <div key={colIdx}>
                            {column.title && (
                              <h4 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 font-season-mix mb-4 text-left">
                                {column.title}
                              </h4>
                            )}
                            <div className="space-y-4">
                              {column.items.map((item) => (
                                <Link
                                  key={item.id}
                                  to={item.path}
                                  onClick={() => setActiveDropdown(null)}
                                  className="flex items-center gap-3 rounded-2xl transition-all duration-200 hover:bg-slate-50 p-2 -mx-2 group"
                                  data-testid={`nav-dropdown-item-${item.id}`}
                                >
                                  <div className={`p-2 rounded-xl ${item.color} flex-shrink-0 transition-colors duration-200 group-hover:scale-105`}>
                                    <item.icon size={18} />
                                  </div>
                                  <div className="flex flex-col text-left">
                                    <span className="text-[13px] font-bold text-slate-800 group-hover:text-[#243b7f] transition-colors leading-snug">
                                      {item.name}
                                    </span>
                                    {item.sub && (
                                      <span className="text-xs text-slate-400 line-clamp-1 mt-1 font-season-mix">
                                        {item.sub}
                                      </span>
                                    )}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right Callout Card - Horizontal Card styled exactly like the 2nd image */}
                      <div className={`${
                        activeLink.columns.length === 3 ? "col-span-4" : "col-span-5"
                      }`}>
                        <Link
                          to={activeLink.callout.linkPath}
                          onClick={() => setActiveDropdown(null)}
                          className="block bg-white border border-slate-100 rounded-3xl p-3 flex gap-4 items-center shadow-sm hover:shadow-md hover:border-slate-200/60 transition-all duration-300 group h-full"
                        >
                          {/* Gradient Box on Left */}
                          <div className={`rounded-2xl ${
                            activeLink.columns.length === 3 ? "w-[130px] h-[110px]" : "w-[160px] h-[130px]"
                          } bg-gradient-to-br ${activeLink.callout.gradient} flex items-center justify-center relative overflow-hidden flex-shrink-0 shadow-sm`}>
                            {/* Faint grid overlay */}
                            <div className="absolute inset-0 bg-white/[0.04] opacity-40 pointer-events-none" />
                            {/* Huge centered text */}
                            <span className={`font-black uppercase tracking-wider text-white drop-shadow-sm px-2 text-center select-none ${
                              activeLink.columns.length === 3 ? "text-sm" : "text-base"
                            }`}>
                              {activeLink.callout.title}
                            </span>
                          </div>

                          {/* Text on Right */}
                          <div className="flex flex-col text-left justify-center flex-1">
                            <span className="text-base font-bold text-slate-800 group-hover:text-[#243b7f] transition-colors leading-snug">
                              {activeLink.callout.linkText}
                            </span>
                            <span className="text-[12px] text-slate-500 mt-2.5 leading-relaxed font-season-mix">
                              {activeLink.callout.desc}
                            </span>
                          </div>
                        </Link>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="xl:hidden w-full overflow-hidden bg-white border-t border-slate-100"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-6 max-h-[80vh] overflow-y-auto">
                <div className="flex flex-col gap-3">
                  {navLinks.map((link, index) => {
                    if (link.isDropdown) {
                      const isDropdownActive = link.dropdownId === "industries" 
                        ? location.pathname.startsWith("/industries")
                        : location.pathname.startsWith("/expertise");
                      const isThisMobileOpen = activeMobileDropdown === link.dropdownId;

                      return (
                        <motion.div
                          key={link.name}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex flex-col"
                        >
                          <button
                            onClick={() => setActiveMobileDropdown(isThisMobileOpen ? null : link.dropdownId)}
                            className={`relative flex items-center justify-between w-full py-3 px-4 rounded-xl text-[14px] font-bold uppercase tracking-wider transition-colors ${
                              isDropdownActive
                                ? "bg-[#243b7f]/5 text-[#243b7f]"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                            data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <span>{link.name}</span>
                            <ChevronDown
                              size={16}
                              className={`transition-transform duration-300 ${
                                isThisMobileOpen ? "rotate-180" : ""
                              }`}
                            />
                            {isDropdownActive && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#f7c550] rounded-r-full" />
                            )}
                          </button>
                          
                          {/* Collapsible Mobile Items */}
                          <AnimatePresence>
                            {isThisMobileOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden bg-slate-50/50 rounded-2xl mt-2 ml-4 border-l-2 border-slate-100"
                              >
                                <div className="py-2 px-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {link.columns.flatMap(col => col.items).map((item) => (
                                    <Link
                                      key={item.id}
                                      to={item.path}
                                      onClick={() => {
                                        setIsOpen(false);
                                        setActiveMobileDropdown(null);
                                      }}
                                      className={`flex items-center gap-3 py-2.5 px-3 rounded-xl text-[13px] font-semibold transition-colors ${
                                        location.pathname === item.path
                                          ? "text-[#243b7f] bg-[#243b7f]/5"
                                          : "text-slate-600 hover:text-[#243b7f] hover:bg-slate-50"
                                      }`}
                                      data-testid={`mobile-nav-dropdown-item-${item.id}`}
                                    >
                                      <div className="p-1.5 rounded-lg bg-white shadow-sm flex-shrink-0 mt-0.5">
                                        <item.icon size={14} className="text-[#243b7f]" />
                                      </div>
                                      <div className="flex flex-col text-left">
                                        <span className="font-bold text-slate-800 leading-snug">{item.name}</span>
                                        {item.sub && (
                                          <span className="text-xs text-slate-400 font-normal mt-0.5 line-clamp-1">
                                            {item.sub}
                                          </span>
                                        )}
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    }

                    return (
                      <motion.div
                        key={link.path}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={link.path}
                          onClick={() => setIsOpen(false)}
                          className={`relative block py-3 px-4 rounded-xl text-[14px] font-bold uppercase tracking-wider transition-colors ${
                            isActive(link.path)
                              ? "bg-[#243b7f]/5 text-[#243b7f]"
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                          data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {link.name}
                          {isActive(link.path) && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#f7c550] rounded-r-full" />
                          )}
                        </Link>
                      </motion.div>
                    );
                  })}
                  
                  {/* Stacked Mobile CTAs */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: navLinks.length * 0.05 }}
                    className="mt-4 flex flex-col sm:flex-row gap-2"
                  >
                    <Link to="/contact" className="flex-1" onClick={() => setIsOpen(false)}>
                      <Button
                        className="w-full bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full py-3 text-[13px] font-bold uppercase tracking-wider"
                        data-testid="mobile-nav-cta"
                      >
                        Get Started
                      </Button>
                    </Link>
                    <Link to="/contact" className="flex-1" onClick={() => setIsOpen(false)}>
                      <Button
                        variant="outline"
                        className="w-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-full py-3 text-[13px] font-bold uppercase tracking-wider"
                        data-testid="mobile-nav-contact"
                      >
                        Contact Us
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}
